import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { Button } from './button';
import { Input } from './input';
import { cn } from '../../lib/utils';
import { ChevronDown, ChevronRight, Search, X, Check, FolderTree } from 'lucide-react';

/**
 * TreeCategorySelect - Selector de categorías con árbol colapsable y buscador
 */
export const TreeCategorySelect = ({ 
  value, 
  onValueChange, 
  categorias = [], 
  placeholder = "Seleccionar categoría",
  className,
  disabled = false
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [expandedPadres, setExpandedPadres] = useState({});
  const inputRef = useRef(null);

  // Organizar categorías jerárquicamente
  const estructura = useMemo(() => {
    const padres = categorias.filter(c => !c.padre_id);
    const hijos = categorias.filter(c => c.padre_id);
    
    if (padres.length === 0 || padres.length === categorias.length) {
      return { tipo: 'plana', items: categorias };
    }
    
    const hijosPorPadre = {};
    hijos.forEach(h => {
      if (!hijosPorPadre[h.padre_id]) hijosPorPadre[h.padre_id] = [];
      hijosPorPadre[h.padre_id].push(h);
    });
    
    return { 
      tipo: 'jerarquica', 
      padres: padres.map(p => ({
        ...p,
        hijos: hijosPorPadre[p.id] || []
      }))
    };
  }, [categorias]);

  // Filtrar por búsqueda
  const filteredEstructura = useMemo(() => {
    if (!search.trim()) return estructura;
    
    const searchLower = search.toLowerCase();
    
    if (estructura.tipo === 'plana') {
      return {
        tipo: 'plana',
        items: estructura.items.filter(c => c.nombre.toLowerCase().includes(searchLower))
      };
    }
    
    // Para jerárquica, filtrar padres que coincidan o tengan hijos que coincidan
    const filteredPadres = estructura.padres
      .map(padre => {
        const padreMatch = padre.nombre.toLowerCase().includes(searchLower);
        const hijosMatch = padre.hijos.filter(h => h.nombre.toLowerCase().includes(searchLower));
        
        if (padreMatch || hijosMatch.length > 0) {
          return {
            ...padre,
            hijos: padreMatch ? padre.hijos : hijosMatch,
            _forceExpand: true // Expandir automáticamente cuando hay búsqueda
          };
        }
        return null;
      })
      .filter(Boolean);
    
    return { tipo: 'jerarquica', padres: filteredPadres };
  }, [estructura, search]);

  // Encontrar categoría seleccionada
  const categoriaSeleccionada = useMemo(() => {
    if (!value) return null;
    const cat = categorias.find(c => c.id?.toString() === value?.toString());
    if (!cat) return null;
    const padre = cat.padre_id ? categorias.find(p => p.id === cat.padre_id) : null;
    return { ...cat, padreNombre: padre?.nombre };
  }, [value, categorias]);

  // Toggle expandir padre
  const togglePadre = (padreId, e) => {
    e.stopPropagation();
    setExpandedPadres(prev => ({ ...prev, [padreId]: !prev[padreId] }));
  };

  // Seleccionar categoría
  const handleSelect = (cat) => {
    onValueChange(cat.id.toString());
    setOpen(false);
    setSearch('');
  };

  // Focus en input al abrir
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  // Determinar si padre está expandido
  const isPadreExpanded = (padre) => {
    if (search.trim() && padre._forceExpand) return true;
    return expandedPadres[padre.id] || false;
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "w-full justify-between font-normal h-9",
            !categoriaSeleccionada && "text-muted-foreground",
            categoriaSeleccionada && "text-gray-900",
            className
          )}
        >
          <span className="truncate">
            {categoriaSeleccionada ? (
              <span className="text-gray-900 font-normal">{categoriaSeleccionada.nombre}</span>
            ) : placeholder}
          </span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start">
        {/* Buscador */}
        <div className="p-2 border-b">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
            <Input
              ref={inputRef}
              placeholder="Buscar categoría..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 h-9"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-2 top-2.5 text-gray-400 hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        {/* Lista de categorías */}
        <div className="max-h-64 overflow-y-auto p-1">
          {filteredEstructura.tipo === 'plana' ? (
            // Lista plana
            filteredEstructura.items.length === 0 ? (
              <div className="text-center py-4 text-gray-400 text-sm">
                No se encontraron categorías
              </div>
            ) : (
              filteredEstructura.items.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => handleSelect(cat)}
                  className={cn(
                    "w-full text-left px-3 py-2 rounded-md text-sm hover:bg-gray-100 flex items-center justify-between",
                    value?.toString() === cat.id?.toString() && "bg-emerald-50 text-emerald-700"
                  )}
                >
                  {cat.nombre}
                  {value?.toString() === cat.id?.toString() && <Check className="h-4 w-4" />}
                </button>
              ))
            )
          ) : (
            // Lista jerárquica
            filteredEstructura.padres.length === 0 ? (
              <div className="text-center py-4 text-gray-400 text-sm">
                No se encontraron categorías
              </div>
            ) : (
              filteredEstructura.padres.map(padre => (
                <div key={padre.id} className="mb-1">
                  {/* Categoría padre - header clickeable */}
                  <button
                    onClick={(e) => togglePadre(padre.id, e)}
                    className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-gray-100 text-sm font-medium text-gray-700"
                  >
                    {isPadreExpanded(padre) ? (
                      <ChevronDown className="h-4 w-4 text-gray-400" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-gray-400" />
                    )}
                    <FolderTree className="h-4 w-4 text-emerald-500" />
                    <span className="flex-1 text-left">{padre.nombre}</span>
                    <span className="text-xs text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">
                      {padre.hijos.length}
                    </span>
                  </button>
                  
                  {/* Subcategorías */}
                  {isPadreExpanded(padre) && padre.hijos.length > 0 && (
                    <div className="ml-4 mt-1 border-l-2 border-gray-100 pl-2">
                      {padre.hijos.map(hijo => (
                        <button
                          key={hijo.id}
                          onClick={() => handleSelect(hijo)}
                          className={cn(
                            "w-full text-left px-3 py-1.5 rounded-md text-sm hover:bg-gray-100 flex items-center justify-between",
                            value?.toString() === hijo.id?.toString() && "bg-emerald-50 text-emerald-700"
                          )}
                        >
                          <span className="flex items-center gap-2">
                            <span className="w-1.5 h-1.5 rounded-full bg-gray-300"></span>
                            {hijo.nombre}
                          </span>
                          {value?.toString() === hijo.id?.toString() && <Check className="h-4 w-4" />}
                        </button>
                      ))}
                    </div>
                  )}
                  
                  {/* Si padre no tiene hijos */}
                  {isPadreExpanded(padre) && padre.hijos.length === 0 && (
                    <div className="ml-8 py-2 text-xs text-gray-400 italic">
                      Sin subcategorías
                    </div>
                  )}
                </div>
              ))
            )
          )}
        </div>

        {/* Limpiar selección */}
        {value && (
          <div className="border-t p-2">
            <button
              onClick={() => { onValueChange(''); setOpen(false); }}
              className="w-full text-left px-3 py-1.5 rounded-md text-sm text-gray-500 hover:bg-gray-100"
            >
              Limpiar selección
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default TreeCategorySelect;
